import {
  type BrowserHelperConfig,
  isBrowserHelperConfig,
} from "@/lib/court-link-helper";
import {
  LiveLinkResolveError,
  buildLiveLinkExtensionHref,
  buildLiveLinkHref,
} from "@/lib/live-link";
import {
  LiveLinkCopyConfirmDialog,
  useLiveLinkAction,
} from "./live-link-action";

interface LiveLinkAnswerProps {
  /** The single-line text value the registrant entered. */
  value: string;
  /** URL template configured on the question (contains a `{{value}}` placeholder). */
  urlTemplate: string | null | undefined;
  /** When true, clicking the link copies the value and shows a confirmation dialog. */
  copyValueToClipboard: boolean;
  httpMethod?: string | null;
  valueExtractRegex?: string | null;
  requestFields?: Record<string, string> | null;
  browserHelperConfig?: BrowserHelperConfig | Record<string, unknown> | null;
}

/**
 * Renders a Live Link question answer as a clickable link to the resolved URL.
 *
 * When copy-to-clipboard is enabled, clicking copies the value and shows a
 * confirmation dialog before opening the external URL.
 */
export function LiveLinkAnswer({
  value,
  urlTemplate,
  copyValueToClipboard,
  httpMethod,
  valueExtractRegex,
  requestFields,
  browserHelperConfig,
}: LiveLinkAnswerProps) {
  const method = String(httpMethod ?? "GET")
    .trim()
    .toUpperCase();
  const { href, resolveError } = (() => {
    try {
      // For POST live links the url_template is an encrypt/API endpoint, not a
      // browseable page. Avoid advertising it as the anchor href (middle-click /
      // open-in-new-tab would show raw JSON). Resolution happens on click.
      if (method === "POST") {
        return { href: "#", resolveError: null };
      }
      if (method === "EXTENSION") {
        return {
          href: buildLiveLinkExtensionHref(urlTemplate, value, {
            valueExtractRegex,
            requestFields,
            browserHelperConfig: isBrowserHelperConfig(browserHelperConfig)
              ? browserHelperConfig
              : null,
          }),
          resolveError: null,
        };
      }
      return {
        href: buildLiveLinkHref(urlTemplate, value, {
          valueExtractRegex,
          requestFields,
        }),
        resolveError: null,
      };
    } catch (error) {
      if (error instanceof LiveLinkResolveError) {
        return { href: null, resolveError: error.message };
      }
      throw error;
    }
  })();
  const {
    isCopyDialogOpen,
    setIsCopyDialogOpen,
    isResolving,
    trigger,
    handleProceed,
  } = useLiveLinkAction({
    value,
    urlTemplate,
    copyValueToClipboard,
    httpMethod,
    valueExtractRegex,
    requestFields,
    browserHelperConfig,
  });

  // No template configured (or empty): show the plain value so it is still visible.
  if (!urlTemplate?.trim()) {
    return <p className="text-sm">{value}</p>;
  }

  if (!href && method !== "POST") {
    return (
      <p
        className="text-sm text-destructive"
        title={resolveError ?? "Live Link configuration error"}
      >
        {value}
      </p>
    );
  }

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    if (isResolving) {
      return;
    }
    trigger();
  };

  return (
    <>
      <a
        href={href ?? "#"}
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleClick}
        className="text-sm text-primary underline underline-offset-2 hover:text-primary/80 break-all"
        aria-disabled={isResolving}
      >
        {value}
      </a>

      {copyValueToClipboard ? (
        <LiveLinkCopyConfirmDialog
          open={isCopyDialogOpen}
          onOpenChange={setIsCopyDialogOpen}
          value={value}
          onProceed={handleProceed}
          isResolving={isResolving}
        />
      ) : null}
    </>
  );
}
